import type { PDFFont, PDFPage } from 'pdf-lib';
import {
  BlendMode,
  PDFDocument,
  rgb,
  StandardFonts,
} from 'pdf-lib';

import type { PdfEdit, PdfRect, PdfTextStyle } from './types';

/**
 * Chars encodable with pdf-lib's standard (WinAnsi) fonts: latin-1 plus
 * the cp1252 extras. Anything else is replaced before drawing so a single
 * exotic character can't fail the whole export.
 */
 
const NON_WIN_ANSI = /[^ -ÿ€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]/g;

function sanitizeWinAnsi(text: string): string {
  return text.replace(NON_WIN_ANSI, '?');
}

function parseHexColor(color: string) {
  const hex = color.replace('#', '');
  const value = parseInt(
    hex.length === 3
      ? hex
          .split('')
          .map(c => c + c)
          .join('')
      : hex,
    16
  );
  if (Number.isNaN(value)) return rgb(0, 0, 0);
  return rgb(
    ((value >> 16) & 0xff) / 255,
    ((value >> 8) & 0xff) / 255,
    (value & 0xff) / 255
  );
}

type FontKey = `${'helvetica' | 'times' | 'courier'}:${'normal' | 'bold'}`;

const STANDARD_FONTS: Record<FontKey, StandardFonts> = {
  'helvetica:normal': StandardFonts.Helvetica,
  'helvetica:bold': StandardFonts.HelveticaBold,
  'times:normal': StandardFonts.TimesRoman,
  'times:bold': StandardFonts.TimesRomanBold,
  'courier:normal': StandardFonts.Courier,
  'courier:bold': StandardFonts.CourierBold,
};

class FontPool {
  private readonly fonts = new Map<FontKey, PDFFont>();

  constructor(private readonly doc: PDFDocument) {}

  async get(style: PdfTextStyle): Promise<PDFFont> {
    const family = style.fontFamily.toLowerCase();
    const base = family.includes('times')
      ? 'times'
      : family.includes('courier') || family.includes('mono')
        ? 'courier'
        : 'helvetica';
    const weight =
      style.fontWeight >= 600 || family.includes('bold') ? 'bold' : 'normal';
    const key: FontKey = `${base}:${weight}`;

    let font = this.fonts.get(key);
    if (!font) {
      font = await this.doc.embedFont(STANDARD_FONTS[key]);
      this.fonts.set(key, font);
    }
    return font;
  }
}

function pageRect(page: PDFPage, rect: PdfRect) {
  // Our edit coordinates use a top-left page origin; pdf-lib uses bottom-left.
  return {
    x: rect.x,
    y: page.getHeight() - rect.y - rect.h,
    width: rect.w,
    height: rect.h,
  };
}

const WHITEOUT_PADDING = 0.5;

function drawWhiteout(page: PDFPage, rects: PdfRect[]) {
  for (const rect of rects) {
    const { x, y, width, height } = pageRect(page, rect);
    page.drawRectangle({
      x: x - WHITEOUT_PADDING,
      y: y - WHITEOUT_PADDING,
      width: width + WHITEOUT_PADDING * 2,
      height: height + WHITEOUT_PADDING * 2,
      color: rgb(1, 1, 1),
    });
  }
}

function drawText(
  page: PDFPage,
  font: PDFFont,
  text: string,
  box: PdfRect,
  style: PdfTextStyle
) {
  if (!text.trim()) return;
  const size = style.fontSize;
  const lineHeight = size * 1.2;
  page.drawText(sanitizeWinAnsi(text), {
    x: box.x,
    // drawText's y is the baseline of the first line.
    y: page.getHeight() - box.y - size,
    size,
    font,
    color: parseHexColor(style.color),
    lineHeight,
    maxWidth: box.w > 0 ? box.w : undefined,
  });
}

function drawInk(
  page: PDFPage,
  points: number[],
  color: string,
  strokeWidth: number
) {
  if (points.length < 4) return;
  let path = `M ${points[0]} ${points[1]}`;
  for (let i = 2; i + 1 < points.length; i += 2) {
    path += ` L ${points[i]} ${points[i + 1]}`;
  }
  // drawSvgPath interprets path coordinates with y growing downward,
  // so anchoring at the page top maps our top-left origin directly.
  page.drawSvgPath(path, {
    x: 0,
    y: page.getHeight(),
    borderColor: parseHexColor(color),
    borderWidth: strokeWidth,
    borderLineCap: 1,
  });
}

function drawHighlight(page: PDFPage, rects: PdfRect[], color: string) {
  for (const rect of rects) {
    page.drawRectangle({
      ...pageRect(page, rect),
      color: parseHexColor(color),
      opacity: 0.35,
      blendMode: BlendMode.Multiply,
    });
  }
}

/**
 * Bakes the collaborative edit list into the base PDF and returns the
 * bytes of the edited document. The base PDF is never mutated in place —
 * exporting always derives a fresh document.
 */
export async function exportEditedPdf(
  data: ArrayBuffer | Uint8Array,
  edits: PdfEdit[]
): Promise<Uint8Array> {
  const doc = await PDFDocument.load(data);
  const fonts = new FontPool(doc);
  const pageCount = doc.getPageCount();

  const ordered = [...edits].sort(
    (a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0)
  );

  for (const edit of ordered) {
    if (edit.page < 0 || edit.page >= pageCount) continue;
    const page = doc.getPage(edit.page);

    switch (edit.type) {
      case 'text-replace': {
        drawWhiteout(page, edit.rects);
        const font = await fonts.get(edit.style);
        drawText(page, font, edit.text.toString(), edit.box, edit.style);
        break;
      }
      case 'text-add': {
        const font = await fonts.get(edit.style);
        drawText(page, font, edit.text.toString(), edit.box, edit.style);
        break;
      }
      case 'whiteout':
        drawWhiteout(page, edit.rects);
        break;
      case 'ink':
        drawInk(page, edit.points, edit.color, edit.strokeWidth);
        break;
      case 'highlight':
        drawHighlight(page, edit.rects, edit.color);
        break;
    }
  }

  return doc.save();
}
